import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, MapPin, Briefcase, X, Loader2 } from "lucide-react";

/**
 * <DispatchSuggest workOrderId onAssign /> — ServiceTitan-style "who's best?"
 * popover. Calls GET /api/dispatch/suggest/{wo_id} and shows ranked crew
 * with proximity + load. Clicking "Assign" calls back to the parent to
 * schedule with default time slot (next morning 9am).
 */
export default function DispatchSuggest({ workOrderId, onAssign, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/dispatch/suggest/${workOrderId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => console.warn("dispatch suggest:", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workOrderId]);

  const handleAssign = async (employeeId) => {
    setAssigning(employeeId);
    try {
      await onAssign(employeeId);
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div
      className="absolute z-30 top-full left-0 mt-2 w-80 bg-white dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-700 rounded-lg shadow-2xl overflow-hidden"
      data-testid="dispatch-suggest"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-[#0A2540] to-[#1E3A66] text-white">
        <div className="flex items-center gap-1.5 text-xs font-semibold tracking-wide">
          <Sparkles className="w-3.5 h-3.5 text-[#C9A356]" />
          SMART DISPATCH
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="dispatch-suggest-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="px-4 py-8 text-center text-sm text-[#475569] dark:text-slate-400">
          <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2 text-[#C9A356]" />
          Scoring crew…
        </div>
      ) : !data || data.suggestions.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-[#475569] dark:text-slate-400">
          No active crew to suggest.
        </div>
      ) : (
        <>
          <div className="px-4 py-2 text-[11px] text-[#94A3B8] border-b border-[#E2E8F0] dark:border-slate-800">
            <div className="flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              <span className="truncate">{data.work_order_title}</span>
            </div>
            {data.job_location?.address && (
              <div className="flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{data.job_location.address}</span>
              </div>
            )}
          </div>
          <ul className="max-h-[280px] overflow-y-auto">
            {data.suggestions.map((s, idx) => (
              <li key={s.employee_id} className="border-b border-[#E2E8F0] dark:border-slate-800 last:border-0">
                <div className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <span className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${
                    idx === 0 ? "bg-[#C9A356] text-[#0A2540]" : "bg-slate-100 dark:bg-slate-800 text-[#475569] dark:text-slate-300"
                  }`}>
                    {(s.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{s.name}</div>
                      {idx === 0 && <span className="px-1.5 py-0.5 rounded-full bg-[#C9A356]/15 text-[#C9A356] text-[9px] font-bold tracking-wider">BEST</span>}
                    </div>
                    <div className="text-[10px] text-[#475569] dark:text-slate-400 truncate">{s.reason}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold tabular-nums text-[#0A2540] dark:text-white">{Math.round(s.score * 100)}</div>
                    <button
                      type="button"
                      onClick={() => handleAssign(s.employee_id)}
                      disabled={assigning === s.employee_id}
                      data-testid={`suggest-assign-${s.employee_id}`}
                      className="text-[10px] text-[#C9A356] font-semibold hover:underline disabled:opacity-50"
                    >
                      {assigning === s.employee_id ? "…" : "Assign"}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 text-[10px] text-[#94A3B8] bg-slate-50 dark:bg-slate-900 border-t border-[#E2E8F0] dark:border-slate-800">
            Scored 60% proximity · 40% current load.
          </div>
        </>
      )}
    </div>
  );
}
